import { Routes, Route } from "react-router-dom";
import React, { Suspense } from "react";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import { RootLayout } from "./layouts/RootLayout";
import {
  RootRoute,
  LoginRoute,
  MaintenanceRoute,
  MaintenanceRestrictedRoute,
  UnauthorizedRoute,
  ForbiddenRoute,
  NoAccessRoute,
  AccountInactiveRoute,
} from "./routes";

const AdminCompaniesRoute = React.lazy(() => import("./routes/authenticated/admin/companies"));
const AdminCompanyDetailRoute = React.lazy(
  () => import("./routes/authenticated/admin/company-detail"),
);
const AdminLayout = React.lazy(() => import("./routes/authenticated/admin/layout"));
const AdminUsersRoute = React.lazy(() => import("./routes/authenticated/admin/users"));
const AnalyticsLayout = React.lazy(() => import("./routes/authenticated/analytics-layout"));
const AnalyticsRoute = React.lazy(() => import("./routes/authenticated/analytics"));
const AuthTestRoute = React.lazy(() => import("./routes/authenticated/auth-test"));
const AuthenticatedLayout = React.lazy(() => import("./routes/authenticated/layout"));
const ChatRoute = React.lazy(() => import("./routes/authenticated/chat"));
const DataReviewRoute = React.lazy(() => import("./routes/authenticated/data-review"));
const PlansActualsRoute = React.lazy(() => import("./routes/authenticated/plans-actuals"));
const PlansActualsReportRoute = React.lazy(
  () => import("./routes/authenticated/plans-actuals-report"),
);
const DatasetsRoute = React.lazy(() => import("./routes/authenticated/datasets"));
const FlagsRoute = React.lazy(() => import("./routes/authenticated/flags"));
const ProfileRoute = React.lazy(() => import("./routes/authenticated/profile"));
const UiPreviewRoute = React.lazy(() => import("./routes/ui-preview"));

function AppLoadingFallback({ className }: { className: string | undefined }) {
  return (
    <div className={cn("h-full w-full flex flex-col items-center justify-center gap-4", className)}>
      <Spinner size="lg" label="Loading" />
      <p className="text-gray-500">Loading...</p>
    </div>
  );
}

function LazyRoute({
  className,
  component: Component,
}: {
  className?: string;
  component: React.LazyExoticComponent<React.ComponentType>;
}) {
  return (
    <Suspense fallback={<AppLoadingFallback className={className} />}>
      <Component />
    </Suspense>
  );
}

export default function App() {
  return (
    <Routes>
      <Route element={<RootLayout />}>
        <Route path="/" element={<RootRoute />} />
        <Route path="/login" element={<LoginRoute />} />
        <Route path="/maintenance" element={<MaintenanceRoute />} />
        <Route path="/maintenance-restricted" element={<MaintenanceRestrictedRoute />} />
        <Route path="/unauthorized" element={<UnauthorizedRoute />} />
        <Route path="/forbidden" element={<ForbiddenRoute />} />
        <Route path="/no-access" element={<NoAccessRoute />} />
        <Route path="/account-inactive" element={<AccountInactiveRoute />} />
        <Route
          path="/ui-preview"
          element={<LazyRoute className="h-screen" component={UiPreviewRoute} />}
        />

        <Route element={<LazyRoute className="h-screen" component={AuthenticatedLayout} />}>
          <Route path="/analytics" element={<LazyRoute component={AnalyticsLayout} />}>
            <Route index element={<LazyRoute component={AnalyticsRoute} />} />
          </Route>
          <Route path="/chat" element={<LazyRoute component={ChatRoute} />} />
          <Route path="/plans-actuals/data" element={<LazyRoute component={PlansActualsRoute} />} />
          <Route
            path="/plans-actuals/report"
            element={<LazyRoute component={PlansActualsReportRoute} />}
          />
          <Route path="/datasets" element={<LazyRoute component={DatasetsRoute} />} />
          <Route path="/data-review" element={<LazyRoute component={DataReviewRoute} />} />
          <Route path="/flags" element={<LazyRoute component={FlagsRoute} />} />
          <Route path="/profile" element={<LazyRoute component={ProfileRoute} />} />
          <Route path="/auth-test" element={<LazyRoute component={AuthTestRoute} />} />
          <Route path="/admin" element={<LazyRoute component={AdminLayout} />}>
            <Route path="users" element={<LazyRoute component={AdminUsersRoute} />} />
            <Route path="companies" element={<LazyRoute component={AdminCompaniesRoute} />} />
            <Route
              path="companies/:companyId"
              element={<LazyRoute component={AdminCompanyDetailRoute} />}
            />
          </Route>
        </Route>
      </Route>
    </Routes>
  );
}
